import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bot, Loader2, MessageCircle, Send, X } from 'lucide-react';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';

const initialMessage = {
  role: 'assistant',
  content: 'Oi! Eu sou o BOT do Sistema CRAS 🤖🐾\n\nPosso te ajudar com dúvidas sobre telas, cadastros, prontuário, permissões, relatórios e rotina do sistema.',
};

export default function AssistantBot() {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [isOpen, setIsOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [messages, setMessages] = useState([initialMessage]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const userIdRef = useRef(userId);

  useEffect(() => {
    userIdRef.current = userId;
    setMessages([initialMessage]);
    setQuestion('');
    setLoading(false);
    setUsage(null);
    if (!userId) {
      setIsOpen(false);
      return;
    }
    fetchUsage();
  }, [userId]);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/assistant/usage`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching assistant usage:', error);
    }
  };

  const remaining = usage?.remaining ?? 0;
  const limit = usage?.limit ?? 0;
  const hasRemaining = !usage || remaining > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || loading || !hasRemaining) return;
    const requestUserId = userId;

    setQuestion('');
    setLoading(true);
    setMessages((current) => [...current, { role: 'user', content: trimmedQuestion }]);

    try {
      const response = await axios.post(`${API}/assistant/chat`, { question: trimmedQuestion });
      if (requestUserId !== userIdRef.current) return;
      setUsage({
        remaining: response.data.remaining,
        limit: response.data.limit,
        used: response.data.used,
        reset_date: response.data.reset_date,
      });
      setMessages((current) => [...current, { role: 'assistant', content: response.data.answer }]);
    } catch (error) {
      if (requestUserId !== userIdRef.current) return;
      const detail = error.response?.data?.detail || 'Não consegui responder agora. Tente novamente em instantes.';
      if (error.response?.data?.remaining !== undefined) {
        setUsage(error.response.data);
      } else {
        fetchUsage();
      }
      setMessages((current) => [...current, { role: 'assistant', content: `${detail} 🙂` }]);
    } finally {
      if (requestUserId === userIdRef.current) {
        setLoading(false);
      }
    }
  };

  if (!user) return null;

  return (
    <div className="assistant-bot">
      {isOpen && (
        <section className="assistant-panel" aria-label="BOT de ajuda do Sistema CRAS">
          <header className="assistant-header">
            <div className="assistant-title">
              <span className="assistant-avatar" aria-hidden="true">
                <span className="assistant-avatar-ear left" />
                <Bot className="assistant-avatar-icon" />
                <span className="assistant-avatar-ear right" />
              </span>
              <div>
                <h2>BOT CRAS</h2>
                <p>{usage ? `${remaining}/${limit} consultas hoje` : 'Consultas do dia'}</p>
              </div>
            </div>
            <button className="assistant-icon-button" type="button" onClick={() => setIsOpen(false)} aria-label="Fechar BOT">
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="assistant-messages">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`assistant-message ${message.role}`}>
                {message.content.split('\n').map((line, lineIndex) => (
                  <p key={lineIndex}>{line || '\u00a0'}</p>
                ))}
              </div>
            ))}
            {loading && (
              <div className="assistant-message assistant">
                <p><Loader2 className="assistant-inline-loader" /> Pensando na melhor resposta...</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="assistant-form" onSubmit={handleSubmit}>
            {!hasRemaining && (
              <p className="assistant-limit-message">
                Você usou todas as consultas de hoje. Amanhã elas são renovadas. 🙂
              </p>
            )}
            <div className="assistant-input-row">
              <textarea
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="Pergunte sobre como usar o sistema..."
                maxLength={700}
                rows={2}
                disabled={loading || !hasRemaining}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSubmit(event);
                  }
                }}
              />
              <button className="assistant-send-button" type="submit" disabled={loading || !question.trim() || !hasRemaining} aria-label="Enviar pergunta">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </section>
      )}

      <button className="assistant-toggle" type="button" onClick={() => setIsOpen((current) => !current)} aria-label="Abrir BOT de ajuda">
        <span className="assistant-toggle-face" aria-hidden="true">
          <span className="assistant-toggle-ear left" />
          <Bot className="w-6 h-6" />
          <span className="assistant-toggle-ear right" />
        </span>
        <span className="assistant-toggle-text">BOT</span>
        {usage && <span className="assistant-toggle-count">{remaining}</span>}
        {!isOpen && <MessageCircle className="assistant-toggle-chat" />}
      </button>
    </div>
  );
}
